import { parseApiErrorBody } from "../utils/parseApiError";
import { throwFromErrorBody } from "../utils/apiRequestError";
/**
 * Notifications API Service
 * Frontend contracts for fetching and updating notifications and preferences.
 *
 * NOTE: Endpoints intentionally omit the `/api` prefix because `authenticatedFetch`
 * applies the API base path consistently (see other services like tradeStatisticsApi).
 */

import { authenticatedFetch } from "./apiClient";
import { normalizeIanaTimeZone } from "../utils/ianaTimezones";
import {
  NotificationCategory,
  NotificationItem,
  NotificationListResponse,
  NotificationEventPreference,
  NotificationPreferences,
  NotificationDataPayload,
} from "../types/notifications";
import { getNotificationDisplayHtml } from "../utils/notificationDisplay";

// Toggle to use local mock data instead of calling the backend API.
// This is primarily for storybook, early UI development, or offline work.
const USE_NOTIFICATION_MOCKS = false;

// Set REACT_APP_DEBUG_NOTIFICATIONS=true in .env.local to log request/response for diagnosing send/list issues.
const DEBUG_NOTIFICATIONS = process.env.REACT_APP_DEBUG_NOTIFICATIONS === "true";

function throwNotificationsError(status: number, body: unknown, fallback: string): never {
  throwFromErrorBody(status, body && typeof body === "object" ? body : {}, fallback);
}

function parseNotificationsErrorBody(text: string, status: number, statusText: string): unknown {
  try {
    return text ? JSON.parse(text) : {};
  } catch {
    return { message: text ? `${status} ${statusText} — ${text.slice(0, 150)}` : `${status} ${statusText}` };
  }
}

// Basic mock data to unblock UI work when backend is unavailable.
// Mirrors the structure used by NotificationModal and NotificationsCenter but
// normalized to the backend contract.
const mockNotifications: NotificationItem[] = [
  {
    id: "mock-1",
    category: "trade",
    eventKey: "trade.closed",
    title: "Trade Completed – NVDA LONG",
    bodyMarkdown:
      "NVDA LONG closed – **Profit:** +$450.00 (+12.5%)\n\nEntry: $520.00 → Exit: $585.00",
    statusInApp: "unread",
    createdAt: new Date(Date.now() - 15 * 60 * 1000).toISOString(),
    data: {
      tradeId: "trade-2024-155",
      symbol: "NVDA",
      side: "LONG",
      entryPrice: 520.0,
      exitPrice: 585.0,
      quantity: 10,
      profit: 450.0,
      returnPercent: 12.5,
    },
  },
  {
    id: "mock-2",
    category: "statistics",
    eventKey: "stats.milestone.profit_streak",
    title: "Win Streak Milestone",
    bodyMarkdown:
      "Amazing work – you're on a **7‑trade winning streak** with total profit of **$1,250.00**.",
    statusInApp: "unread",
    createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    data: {
      milestone: "win_streak",
      count: 7,
      totalProfit: 1250.0,
    },
  },
  {
    id: "mock-3",
    category: "billing",
    eventKey: "billing.payment_succeeded",
    title: "Payment Received",
    bodyMarkdown:
      "Thanks — we received **99.00 USD**. Your access continues as normal.",
    statusInApp: "read",
    createdAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
    data: {
      amount_paid_display: "99.00",
      currency: "USD",
    },
  },
];

const mockPreferences: NotificationPreferences = {
  inAppEnabled: true,
  emailEnabled: true,
  displayCurrency: "USD",
  supportedCurrencies: ["USD", "EUR", "GBP"],
  perCategory: {
    trade: { inApp: true, email: true },
    statistics: { inApp: true, email: false },
    billing: { inApp: true, email: true },
    system: { inApp: true, email: true },
    marketing: { inApp: false, email: false },
  },
  perEvent: {},
};

export interface FetchNotificationsParams {
  limit?: number;
  category?: NotificationCategory;
  /** Opaque pagination cursor (returned by backend). */
  cursor?: string;
}

/** Default page size for list endpoints when callers omit `limit`. */
export const NOTIFICATIONS_DEFAULT_FETCH_LIMIT = 50;

function readNonEmptyString(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function parseNotificationListPagination(
  json: Record<string, unknown>
): { nextCursor: string | null; hasMore: boolean } {
  // Backend encodes cursors as opaque (created_at, id) tokens — never invent one from id alone.
  const nextCursor =
    readNonEmptyString(json.next_cursor) ?? readNonEmptyString(json.nextCursor);

  if (json.has_more === false || json.hasMore === false) {
    return { nextCursor: null, hasMore: false };
  }
  if (nextCursor) {
    return { nextCursor, hasMore: true };
  }
  return { nextCursor: null, hasMore: false };
}

/**
 * Fetch notifications for the current user.
 */
export const fetchNotifications = async (
  params: FetchNotificationsParams = {}
): Promise<NotificationListResponse> => {
  if (USE_NOTIFICATION_MOCKS) {
    const filtered =
      params.category != null
        ? mockNotifications.filter((n) => n.category === params.category)
        : mockNotifications;
    const limit = params.limit ?? 5;
    const notifications = filtered
      .slice()
      .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))
      .slice(0, limit);

    const unreadCount = notifications.filter(
      (n) => n.statusInApp === "unread"
    ).length;

    return {
      notifications,
      unreadCount,
    };
  }

  const searchParams = new URLSearchParams();
  const limit = params.limit ?? NOTIFICATIONS_DEFAULT_FETCH_LIMIT;
  searchParams.append("limit", String(limit));
  if (params.category) {
    searchParams.append("category", params.category);
  }
  if (params.cursor) {
    searchParams.append("cursor", params.cursor);
  }

  const queryString = searchParams.toString();
  const endpoint = queryString ? `/notifications?${queryString}` : "/notifications";

  if (DEBUG_NOTIFICATIONS) {
    console.log("[Notifications DEBUG] GET", endpoint);
  }
  const response = await authenticatedFetch(endpoint);
  const responseText = await response.text();

  if (DEBUG_NOTIFICATIONS) {
    console.log("[Notifications DEBUG] GET response status:", response.status, "body (first 400 chars):", responseText?.slice(0, 400));
  }

  if (!response.ok) {
    const body = parseNotificationsErrorBody(responseText, response.status, response.statusText);
    throwNotificationsError(response.status, body, `Failed to fetch notifications: ${response.status}`);
  }

  // Backend returns { items: [...], unread_count: number, next_cursor?: string } (snake_case).
  // Normalize to { notifications, unreadCount, nextCursor }.
  const json = responseText ? JSON.parse(responseText) : {};

  if (Array.isArray(json)) {
    return {
      notifications: json.map(mapBackendItemToNotification) as NotificationItem[],
    };
  }

  const items = json.items ?? json.notifications;
  const unreadCount =
    typeof json.unread_count === "number"
      ? json.unread_count
      : json.unreadCount;

  const notifications = Array.isArray(items)
    ? (items.map(mapBackendItemToNotification) as NotificationItem[])
    : [];
  const { nextCursor, hasMore } = parseNotificationListPagination(
    json as Record<string, unknown>
  );
  if (DEBUG_NOTIFICATIONS) {
    console.log("[Notifications DEBUG] GET parsed — items.length:", items?.length ?? 0, "unread_count:", unreadCount, "first id:", notifications[0]?.id ?? "none");
  }
  return {
    notifications,
    unreadCount,
    nextCursor,
    hasMore,
  };
};

/**
 * Mark ALL notifications as read for the current user.
 * Backend: POST /api/notifications/mark-all-read
 */
export const markAllNotificationsRead = async (): Promise<{ updated: number }> => {
  if (USE_NOTIFICATION_MOCKS) {
    return { updated: 0 };
  }

  const response = await authenticatedFetch("/notifications/mark-all-read", {
    method: "POST",
  });

  const text = await response.text().catch(() => "");
  if (!response.ok) {
    const body = parseNotificationsErrorBody(text, response.status, response.statusText);
    throwNotificationsError(response.status, body, `Failed to mark all notifications read: ${response.status}`);
  }

  try {
    const json = text ? JSON.parse(text) : {};
    const updated =
      typeof json?.updated === "number"
        ? json.updated
        : typeof json?.count === "number"
          ? json.count
          : 0;
    return { updated };
  } catch {
    return { updated: 0 };
  }
};

/** Parse `data_json` when the DB/driver returns a JSON string. */
function parseNotificationDataPayload(raw: unknown): NotificationDataPayload | null {
  if (raw == null) return null;
  if (typeof raw === "string") {
    const t = raw.trim();
    if (!t) return null;
    try {
      const parsed = JSON.parse(t) as unknown;
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed as NotificationDataPayload;
      }
    } catch {
      return null;
    }
    return null;
  }
  if (typeof raw === "object" && !Array.isArray(raw)) {
    return raw as NotificationDataPayload;
  }
  return null;
}

/** Map backend notification item (snake_case) to frontend NotificationItem (camelCase). */
function mapBackendItemToNotification(row: Record<string, unknown>): NotificationItem {
  const data = parseNotificationDataPayload(row.data_json ?? row.data);
  const bodyRaw = String(row.body_markdown ?? row.bodyMarkdown ?? "");
  const eventKey = row.event_key != null ? String(row.event_key) : null;
  const base: NotificationItem = {
    id: String(row.id ?? ""),
    userId: row.user_id != null ? String(row.user_id) : undefined,
    category: (row.category as NotificationItem["category"]) ?? "system",
    eventKey,
    title: String(row.title ?? ""),
    bodyMarkdown: bodyRaw,
    data,
    channels: (row.channels as NotificationItem["channels"]) ?? null,
    statusInApp: (row.status_in_app ?? row.statusInApp) as NotificationItem["statusInApp"] ?? "unread",
    statusEmail: (row.status_email ?? row.statusEmail) as NotificationItem["statusEmail"],
    createdAt: String(row.created_at ?? row.createdAt ?? ""),
    sentAt: row.sent_at != null ? String(row.sent_at) : row.sentAt != null ? String(row.sentAt) : null,
    expiresAt: row.expires_at != null ? String(row.expires_at) : row.expiresAt != null ? String(row.expiresAt) : null,
  };
  base.bodyMarkdown = getNotificationDisplayHtml(base);
  return base;
}

/**
 * Fetch unread notifications count for the current user.
 */
export const fetchUnreadNotificationCount = async (): Promise<number> => {
  if (USE_NOTIFICATION_MOCKS) {
    return mockNotifications.filter((n) => n.statusInApp === "unread").length;
  }

  const response = await authenticatedFetch("/notifications/unread-count");

  if (!response.ok) {
    const text = await response.text();
    const body = parseNotificationsErrorBody(text, response.status, response.statusText);
    throwNotificationsError(response.status, body, `Failed to fetch unread count: ${response.status}`);
  }

  const json = await response.json();

  // Backend returns { unread_count: number }; also accept unreadCount or raw number
  if (typeof json?.unread_count === "number") {
    return json.unread_count;
  }
  if (typeof json?.unreadCount === "number") {
    return json.unreadCount;
  }
  if (typeof json === "number") {
    return json;
  }

  return 0;
};

/**
 * Mark a notification as read.
 */
export const markNotificationRead = async (id: string): Promise<void> => {
  if (USE_NOTIFICATION_MOCKS) {
    return;
  }

  const response = await authenticatedFetch(`/notifications/${id}/read`, {
    method: "POST",
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throwNotificationsError(response.status, error && typeof error === "object" ? error : {}, `Failed to mark notification as read: ${response.status}`);
  }
};

/**
 * Mark a notification as unread.
 */
export const markNotificationUnread = async (id: string): Promise<void> => {
  if (USE_NOTIFICATION_MOCKS) {
    return;
  }

  const response = await authenticatedFetch(`/notifications/${id}/unread`, {
    method: "POST",
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throwNotificationsError(response.status, error && typeof error === "object" ? error : {}, `Failed to mark notification as unread: ${response.status}`);
  }
};

/**
 * Delete a notification for the current user.
 */
export const deleteNotification = async (id: string): Promise<void> => {
  if (USE_NOTIFICATION_MOCKS) {
    return;
  }

  const response = await authenticatedFetch(`/notifications/${id}`, {
    method: "DELETE",
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throwNotificationsError(response.status, error && typeof error === "object" ? error : {}, `Failed to delete notification: ${response.status}`);
  }
};

export type BulkDeleteNotificationsParams = {
  ids?: string[];
  allMatching?: boolean;
  category?: NotificationCategory | null;
  unreadOnly?: boolean;
};

/**
 * Soft-delete many notifications in one request.
 * Backend: POST /api/notifications/bulk-delete
 */
export const bulkDeleteNotifications = async (
  params: BulkDeleteNotificationsParams
): Promise<{ deleted: number }> => {
  if (USE_NOTIFICATION_MOCKS) {
    return { deleted: params.ids?.length ?? 0 };
  }

  const body: Record<string, unknown> = {
    all_matching: Boolean(params.allMatching),
    unread_only: Boolean(params.unreadOnly),
  };
  if (params.ids && params.ids.length > 0) {
    body.ids = params.ids;
  }
  if (params.category) {
    body.category = params.category;
  }

  const response = await authenticatedFetch("/notifications/bulk-delete", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  const text = await response.text().catch(() => "");
  if (!response.ok) {
    const parsed = parseNotificationsErrorBody(text, response.status, response.statusText);
    throwNotificationsError(
      response.status,
      parsed,
      `Failed to bulk-delete notifications: ${response.status}`
    );
  }

  try {
    const json = text ? JSON.parse(text) : {};
    const deleted =
      typeof json?.deleted === "number"
        ? json.deleted
        : typeof json?.count === "number"
          ? json.count
          : 0;
    return { deleted };
  } catch {
    return { deleted: 0 };
  }
};

/**
 * Fetch notification preferences for the current user.
 */
export const fetchNotificationPreferences =
  async (): Promise<NotificationPreferences> => {
    if (USE_NOTIFICATION_MOCKS) {
      return mockPreferences;
    }

    const response = await authenticatedFetch("/notifications/preferences");

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throwNotificationsError(response.status, error && typeof error === "object" ? error : {}, `Failed to fetch notification preferences: ${response.status}`);
    }

    const json = (await response.json()) as Record<string, unknown>;
    return mapBackendPreferencesToFrontend(json);
  };

/**
 * Update notification preferences for the current user.
 */
export const updateNotificationPreferences = async (
  prefs: NotificationPreferences
): Promise<NotificationPreferences> => {
  if (USE_NOTIFICATION_MOCKS) {
    return prefs;
  }

  const response = await authenticatedFetch("/notifications/preferences", {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(mapFrontendPreferencesToBackend(prefs)),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throwNotificationsError(response.status, error && typeof error === "object" ? error : {}, `Failed to update notification preferences: ${response.status}`);
  }

  const updated = (await response.json()) as Record<string, unknown>;
  return mapBackendPreferencesToFrontend(updated);
};

export interface NotificationTimezoneResponse {
  timezone: string;
  timezone_source: "auto" | "manual";
  broker_account_id: string | null;
  updated: boolean;
  reason: string;
}

export interface UpsertNotificationTimezoneRequest {
  timezone: string;
  source: "auto" | "manual";
  broker_account_id?: string | null;
}

/** Normalize GET/PUT JSON: snake_case or camelCase; only `"manual"` forces manual (else auto). */
export function parseNotificationTimezoneSource(raw: unknown): "auto" | "manual" {
  const s = typeof raw === "string" ? raw.trim().toLowerCase() : "";
  if (s === "manual") return "manual";
  if (s === "auto" || s === "device" || s === "automatic" || s === "browser") return "auto";
  return "auto";
}

function parseNotificationTimezonePayload(raw: unknown): NotificationTimezoneResponse {
  const root = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const nested =
    root.preference && typeof root.preference === "object"
      ? (root.preference as Record<string, unknown>)
      : root.data && typeof root.data === "object"
        ? (root.data as Record<string, unknown>)
        : null;
  const o = nested ? { ...root, ...nested } : root;

  const timezone = normalizeIanaTimeZone(String(o.timezone ?? ""));
  const rawSource = o.timezone_source ?? o.timezoneSource ?? o.source;
  let timezone_source = parseNotificationTimezoneSource(rawSource);

  const useDeviceRaw =
    o.use_device_timezone ?? o.useDeviceTimezone ?? o.follow_device_timezone ?? o.followDeviceTimezone;
  if (useDeviceRaw === true) timezone_source = "auto";
  if (useDeviceRaw === false) timezone_source = "manual";

  const brokerRaw = o.broker_account_id ?? o.brokerAccountId;
  return {
    timezone: timezone || "UTC",
    timezone_source,
    broker_account_id:
      brokerRaw === null || brokerRaw === undefined ? null : String(brokerRaw),
    updated: Boolean(o.updated),
    reason: typeof o.reason === "string" ? o.reason : "",
  };
}

/**
 * Fetch resolved notification timezone preference.
 */
export const fetchNotificationTimezone = async (
  brokerAccountId?: string
): Promise<NotificationTimezoneResponse> => {
  const params = new URLSearchParams();
  if (brokerAccountId) params.set("broker_account_id", brokerAccountId);
  const endpoint = params.toString()
    ? `/notifications/timezone?${params.toString()}`
    : "/notifications/timezone";

  const response = await authenticatedFetch(endpoint);
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throwNotificationsError(response.status, error && typeof error === "object" ? error : {}, `Failed to fetch notification timezone: ${response.status}`);
  }
  const json = await response.json().catch(() => ({}));
  return parseNotificationTimezonePayload(json);
};

/**
 * Upsert notification timezone preference.
 * Uses `timezone_source` in the JSON body (matches GET and typical FastAPI field names). Older clients sent `source`, which many backends ignore.
 */
export const upsertNotificationTimezone = async (
  payload: UpsertNotificationTimezoneRequest
): Promise<NotificationTimezoneResponse> => {
  const timezone_source = payload.source;
  const timezone = normalizeIanaTimeZone(payload.timezone);
  const response = await authenticatedFetch("/notifications/timezone", {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      timezone,
      timezone_source,
      source: timezone_source,
      use_device_timezone: timezone_source === "auto",
      broker_account_id: payload.broker_account_id ?? null,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throwNotificationsError(response.status, error && typeof error === "object" ? error : {}, `Failed to update notification timezone: ${response.status}`);
  }
  const json = await response.json().catch(() => ({}));
  return parseNotificationTimezonePayload(json);
};

function mapBackendPreferencesToFrontend(json: Record<string, unknown>): NotificationPreferences {
  const perCategoryRaw = (json.per_category ?? json.perCategory ?? {}) as Record<
    string,
    { in_app?: boolean; inApp?: boolean; email?: boolean } | undefined
  >;
  const perEventRaw = (json.per_event ?? json.perEvent ?? {}) as Record<
    string,
    {
      in_app?: boolean;
      inApp?: boolean;
      email?: boolean;
      telegram?: boolean;
      quiet?: { start?: string; end?: string; tz?: string } | null;
      account_scope?: { mode?: "all" | "selected"; account_codes?: string[] };
      accountScope?: { mode?: "all" | "selected"; accountCodes?: string[] };
    } | undefined
  >;

  const perCategory = Object.entries(perCategoryRaw).reduce<
    Partial<Record<NotificationCategory, { inApp: boolean; email: boolean }>>
  >((acc, [key, value]) => {
    if (!value) return acc;
    acc[key as NotificationCategory] = {
      inApp: value.inApp ?? value.in_app ?? true,
      email: value.email ?? true,
    };
    return acc;
  }, {});

  const perEvent = Object.entries(perEventRaw).reduce<Record<string, NotificationEventPreference>>(
    (acc, [eventKey, value]) => {
      if (!value) return acc;
      const scope = (value.accountScope ?? value.account_scope) as
        | { mode?: "all" | "selected"; accountCodes?: string[]; account_codes?: string[] }
        | undefined;
      acc[eventKey] = {
        inApp: value.inApp ?? value.in_app,
        email: value.email,
        ...(typeof value.telegram === "boolean" ? { telegram: value.telegram } : {}),
        ...(value.quiet && value.quiet.start && value.quiet.end
          ? { quiet: { start: value.quiet.start, end: value.quiet.end, tz: value.quiet.tz || "UTC" } }
          : {}),
        accountScope: scope
          ? {
              mode: scope.mode ?? "all",
              accountCodes: scope.accountCodes ?? scope.account_codes ?? [],
            }
          : undefined,
      };
      return acc;
    },
    {}
  );

  const supportedRaw = json.supported_currencies ?? json.supportedCurrencies;
  const supportedCurrencies = Array.isArray(supportedRaw)
    ? supportedRaw.map((c) => String(c).toUpperCase()).filter(Boolean)
    : [];

  const displayRaw = json.display_currency ?? json.displayCurrency;
  const displayCurrency =
    typeof displayRaw === "string" && displayRaw.trim()
      ? String(displayRaw).toUpperCase()
      : "USD";

  return {
    inAppEnabled: (json.inAppEnabled ?? json.in_app_enabled ?? true) as boolean,
    emailEnabled: (json.emailEnabled ?? json.email_enabled ?? true) as boolean,
    telegramEnabled: (json.telegramEnabled ?? json.telegram_enabled ?? true) as boolean,
    displayCurrency,
    supportedCurrencies,
    perCategory,
    perEvent,
  };
}

function mapFrontendPreferencesToBackend(prefs: NotificationPreferences): Record<string, unknown> {
  const perCategory = Object.entries(prefs.perCategory ?? {}).reduce<Record<string, unknown>>(
    (acc, [category, value]) => {
      if (!value) return acc;
      acc[category] = {
        in_app: value.inApp,
        email: value.email,
      };
      return acc;
    },
    {}
  );

  const perEvent = Object.entries(prefs.perEvent ?? {}).reduce<Record<string, unknown>>(
    (acc, [eventKey, value]) => {
      const payload: Record<string, unknown> = {};
      if (typeof value.inApp === "boolean") payload.in_app = value.inApp;
      if (typeof value.email === "boolean") payload.email = value.email;
      if (typeof value.telegram === "boolean") payload.telegram = value.telegram;
      if (value.quiet !== undefined) payload.quiet = value.quiet;
      if (value.accountScope) {
        payload.account_scope = {
          mode: value.accountScope.mode,
          account_codes: value.accountScope.accountCodes ?? [],
        };
      }
      acc[eventKey] = payload;
      return acc;
    },
    {}
  );

  return {
    in_app_enabled: prefs.inAppEnabled,
    email_enabled: prefs.emailEnabled,
    ...(typeof prefs.telegramEnabled === "boolean" ? { telegram_enabled: prefs.telegramEnabled } : {}),
    display_currency: (prefs.displayCurrency || "USD").toUpperCase(),
    per_category: perCategory,
    per_event: perEvent,
  };
}

/**
 * Create/update Resend marketing contact (list=marketing) from the signed-in session.
 * @param source account_signup = marketing box at app signup; dashboard_opt_in = settings (default).
 */
export async function optInMarketingEmails(
  source: "account_signup" | "dashboard_opt_in" = "dashboard_opt_in"
): Promise<void> {
  const response = await authenticatedFetch("/notifications/marketing/email/opt-in", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ source }),
  });
  if (!response.ok) {
    const text = await response.text();
    const body = parseNotificationsErrorBody(text, response.status, response.statusText);
    throwNotificationsError(response.status, body, "Failed to opt into marketing emails");
  }
}

export type MarketingEmailPreference = {
  optedIn: boolean;
  contactExists: boolean;
  email: string | null;
  source: string | null;
};

/** Current marketing preference from Tessera DB (source of truth). */
export async function getMarketingEmailPreference(): Promise<MarketingEmailPreference> {
  const response = await authenticatedFetch("/notifications/marketing/email/preference", {
    method: "GET",
  });
  if (!response.ok) {
    const text = await response.text();
    const body = parseNotificationsErrorBody(text, response.status, response.statusText);
    throwNotificationsError(response.status, body, "Failed to load marketing email preference");
  }
  const data = (await response.json()) as {
    opted_in?: boolean;
    contact_exists?: boolean;
    email?: string | null;
    source?: string | null;
  };
  return {
    optedIn: Boolean(data.opted_in),
    contactExists: Boolean(data.contact_exists),
    email: data.email ?? null,
    source: data.source ?? null,
  };
}

/** Opt out of marketing (DB + leave Resend Marketing segment). */
export async function optOutMarketingEmails(): Promise<void> {
  const response = await authenticatedFetch("/notifications/marketing/email/opt-out", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });
  if (!response.ok) {
    const text = await response.text();
    const body = parseNotificationsErrorBody(text, response.status, response.statusText);
    throwNotificationsError(response.status, body, "Failed to opt out of marketing emails");
  }
}

